import React, { useMemo } from 'react';

import { useUserContext } from '@context/UserContext';
import { FatalErrorProvider } from '@services/errorService';
import { useLocalizationContext, useRouteLocalization } from '@services/localizationService';
import { usePageLoadMessaging } from '@services/messagingService';
import { Header } from '@sharedComponents/Header';
import { PagesStepChain } from '@sharedComponents/PagesStepChain';
import { EPage, ReactFCC } from '@types';
import { CypressTestNamespace } from '@utils/test';

import { getFormattedMessages } from '../../../utils';

import { ICheckoutPageLayoutMessagesProps, IStep } from './model';
import { PageWrapper, ContentWrapper } from './styled';

interface ICheckoutPageLayoutProps extends ICheckoutPageLayoutMessagesProps {
  page: EPage;
  homePageUrlAddress: string;
  steps: IStep[];
  isCallCenterOpened: boolean;
  callCenterPhoneNumber: string;
  callCenterOpeningHours: string;
  skeletonComponent: React.FunctionComponent;
}

export const CheckoutPageLayout: ReactFCC<ICheckoutPageLayoutProps> = ({
  page,
  steps,
  messages,
  homePageUrlAddress,
  callCenterPhoneNumber,
  callCenterOpeningHours,
  isCallCenterOpened,
  skeletonComponent,
  children,
}) => {
  const { translationsReady } = useLocalizationContext();
  useRouteLocalization(page);
  usePageLoadMessaging();
  const {
    state: { cart },
  } = useUserContext();

  const { callCenterNumberMessage, callCenterOpeningHoursMessage } = useMemo(
    () => getFormattedMessages(messages, callCenterPhoneNumber, callCenterOpeningHours, isCallCenterOpened),
    [messages, callCenterPhoneNumber, callCenterOpeningHours, isCallCenterOpened],
  );

  const SkeletonComponent = skeletonComponent;

  if (!translationsReady || !cart) {
    return <SkeletonComponent />;
  }

  return (
    <PageWrapper>
      <Header
        linkUrlAddress={homePageUrlAddress}
        callCenterText={callCenterNumberMessage}
        callCenterOpeningHours={callCenterOpeningHoursMessage}
      />
      <FatalErrorProvider>
        <ContentWrapper>
          <PagesStepChain steps={steps} dataTestId={CypressTestNamespace.ELayoutElements.STEP_CHAIN_WRAPPER} />
          {children}
        </ContentWrapper>
      </FatalErrorProvider>
    </PageWrapper>
  );
};
